import { useState, useEffect, useCallback } from 'react'
import { Box, Button, Heading, HStack, SimpleGrid, IconButton, Text, Input, Stack } from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { Plus, LayoutGrid, Table, ChevronLeft, ChevronRight, Search, ChevronDown } from 'lucide-react'
import { useDebounce } from 'use-debounce'
import { useUnitsPaginated, useDeleteUnit, useUnit, useUnitOwners } from '../hooks/useUnits'
import { useProperties } from '@/features/properties/hooks/useProperties'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { UnitCard } from '../components/UnitCard'
import { UnitTable } from '../components/UnitTable'
import { UnitDetailDrawer } from '../components/UnitDetailDrawer'
import { UnitCreateModal } from '../components/UnitCreateModal'
import { UnitMutationsManagementModal } from '../components/UnitMutationsManagementModal'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import { EmptyState } from '@/shared/components/EmptyState'

type ViewMode = 'cards' | 'table'
type SortField = 'unit_number' | 'unit_type' | 'floor' | 'residence' | 'general_shares'
type SortDirection = 'asc' | 'desc'

const ITEMS_PER_PAGE = 20

export function UnitsPage() {
  const { t } = useTranslation()
  const { data: properties = [] } = useProperties()
  const { data: auth } = useAuth()
  const deleteUnit = useDeleteUnit()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('table')
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [mutationsModalUnitId, setMutationsModalUnitId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortField, setSortField] = useState<SortField>('unit_number')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [filterResidenceId, setFilterResidenceId] = useState<string>('')
  const [filterOwnerName, setFilterOwnerName] = useState<string>('')

  // Debounce owner name search to avoid too many queries
  const [debouncedOwnerName] = useDebounce(filterOwnerName, 500)

  // Fetch units with server-side pagination, filtering, and sorting
  const {
    data: paginatedData,
    isLoading,
    error,
    refetch,
  } = useUnitsPaginated({
    residenceId: filterResidenceId || undefined,
    ownerName: debouncedOwnerName || undefined,
    page: currentPage,
    pageSize: ITEMS_PER_PAGE,
    sortField,
    sortDirection,
  })

  // Extract data from paginated response
  const units = paginatedData?.data || []
  const totalPages = paginatedData?.totalPages || 0
  const totalCount = paginatedData?.count || 0

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filterResidenceId, debouncedOwnerName, sortField, sortDirection])

  // Check delete permissions for each unit
  // Only ADMIN and MANAGER can delete units
  const canDeleteUnit = useCallback((residenceId: string) => {
    if (!auth) return false

    // ADMIN can delete all units
    if (auth.isAdmin) return true

    // MANAGER can delete units in their managed residences
    return auth.isManager(residenceId)
  }, [auth])

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('units.delete_confirm'))) return

    setDeletingId(id)
    try {
      await deleteUnit.mutateAsync(id)
      // Close drawer if the deleted unit was selected
      if (selectedUnitId === id) {
        setIsDrawerOpen(false)
        setSelectedUnitId(null)
      }
    } catch (error) {
      console.error('Error deleting unit:', error)
      alert(t('common.error'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleUnitClick = (id: string) => {
    setSelectedUnitId(id)
    setIsDrawerOpen(true)
  }

  const handleManageMutations = (id: string) => {
    setMutationsModalUnitId(id)
  }

  // Fetch unit and ownerships for mutations modal
  const { data: mutationsUnit } = useUnit(mutationsModalUnitId ?? undefined)
  const { data: mutationsOwnerships } = useUnitOwners(mutationsModalUnitId ?? undefined)

  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
    setSelectedUnitId(null)
  }

  // Navigation functions
  const getCurrentIndex = useCallback(() => {
    if (!selectedUnitId || !units) return -1
    return units.findIndex((u) => u.id === selectedUnitId)
  }, [selectedUnitId, units])

  const handleNavigatePrevious = useCallback(() => {
    if (!units || units.length === 0) return
    const currentIndex = getCurrentIndex()
    if (currentIndex > 0) {
      const previousUnit = units[currentIndex - 1]
      if (previousUnit) {
        setSelectedUnitId(previousUnit.id)
      }
    }
  }, [units, getCurrentIndex])

  const handleNavigateNext = useCallback(() => {
    if (!units || units.length === 0) return
    const currentIndex = getCurrentIndex()
    if (currentIndex >= 0 && currentIndex < units.length - 1) {
      const nextUnit = units[currentIndex + 1]
      if (nextUnit) {
        setSelectedUnitId(nextUnit.id)
      }
    }
  }, [units, getCurrentIndex])

  const hasPrevious = getCurrentIndex() > 0
  const hasNext = getCurrentIndex() >= 0 && getCurrentIndex() < (units?.length ?? 0) - 1

  // Sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New field, default to ascending
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Keyboard navigation
  useEffect(() => {
    if (!isDrawerOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        handleNavigatePrevious()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        handleNavigateNext()
      } else if (e.key === 'Escape') {
        handleDrawerClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isDrawerOpen, handleNavigatePrevious, handleNavigateNext])

  if (isLoading) {
    return <LoadingState message={t('common.loading')} />
  }

  if (error) {
    return <ErrorState message={t('common.error')} onRetry={() => refetch()} />
  }

  if (!units || units.length === 0) {
    return (
      <>
        <EmptyState
          title={t('units.no_units')}
          description={t('units.create_first')}
          action={{
            label: t('units.create'),
            onClick: () => setIsCreateModalOpen(true),
          }}
        />
        <UnitCreateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={(unitId) => {
            setSelectedUnitId(unitId)
            setIsDrawerOpen(true)
          }}
        />
      </>
    )
  }

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="lg">{t('units.title')}</Heading>
        <HStack gap={2}>
          <HStack gap={1} borderWidth="1px" borderRadius="md" p={1}>
            <IconButton
              aria-label={t('units.view_mode.cards')}
              title={t('units.view_mode.cards')}
              size="sm"
              variant={viewMode === 'cards' ? 'solid' : 'ghost'}
              onClick={() => setViewMode('cards')}
            >
              <LayoutGrid size={18} />
            </IconButton>
            <IconButton
              aria-label={t('units.view_mode.table')}
              title={t('units.view_mode.table')}
              size="sm"
              variant={viewMode === 'table' ? 'solid' : 'ghost'}
              onClick={() => setViewMode('table')}
            >
              <Table size={18} />
            </IconButton>
          </HStack>
          <Button colorPalette="brand" onClick={() => setIsCreateModalOpen(true)}>
            <HStack gap={2}>
              <Plus size={20} />
              <span>{t('units.create')}</span>
            </HStack>
          </Button>
        </HStack>
      </HStack>

      {/* Filters */}
      <Stack direction={{ base: 'column', md: 'row' }} gap={4} mb={6}>
        <Field.Root w={{ base: 'full', md: '280px' }}>
          <NativeSelectRoot position="relative">
            <NativeSelectField
              value={filterResidenceId}
              onChange={(e) => setFilterResidenceId(e.target.value)}
              paddingRight="10"
            >
              <option value="">Toutes les résidences</option>
              {properties.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.residence_key
                    ? `${property.residence_key} - ${property.name}`
                    : property.name}
                </option>
              ))}
            </NativeSelectField>
            <Box
              position="absolute"
              right="3"
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
              color="gray.500"
            >
              <ChevronDown size={16} />
            </Box>
          </NativeSelectRoot>
        </Field.Root>

        <Field.Root w={{ base: 'base', md: '600px' }}>
          <HStack>
            <Search size={18} style={{ position: 'absolute', left: '12px', color: '#718096' }} />
            <Input
              placeholder="Rechercher par nom de propriétaire..."
              value={filterOwnerName}
              onChange={(e) => setFilterOwnerName(e.target.value)}
              pl="40px"
              w='310px'
            />
          </HStack>
        </Field.Root>
      </Stack>

      {viewMode === 'cards' ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
          {units.map((unit) => (
            <UnitCard
              key={unit.id}
              unit={unit}
              isSelected={selectedUnitId === unit.id}
              onClick={handleUnitClick}
              onDelete={
                deletingId === unit.id || !canDeleteUnit(unit.residence_id)
                  ? undefined
                  : handleDelete
              }
              onManageMutations={handleManageMutations}
            />
          ))}
        </SimpleGrid>
      ) : (
        <UnitTable
          units={units}
          selectedUnitId={selectedUnitId}
          onUnitClick={handleUnitClick}
          onDelete={deletingId ? undefined : handleDelete}
          onManageMutations={handleManageMutations}
          canDelete={canDeleteUnit}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <HStack justify="space-between" mt={6} pt={6} borderTopWidth="1px">
          <Text fontSize="sm" color="gray.600">
            {t('common.showing')} {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
            {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} {t('common.of')} {totalCount}
          </Text>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
              <span>{t('common.previous')}</span>
            </Button>
            <Text fontSize="sm" fontWeight="medium">
              {currentPage} / {totalPages}
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <span>{t('common.next')}</span>
              <ChevronRight size={16} />
            </Button>
          </HStack>
        </HStack>
      )}

      <UnitCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={(unitId) => {
          setSelectedUnitId(unitId)
          setIsDrawerOpen(true)
        }}
      />

      <UnitDetailDrawer
        unitId={selectedUnitId}
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        onNavigatePrevious={handleNavigatePrevious}
        onNavigateNext={handleNavigateNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
      />

      {mutationsModalUnitId && mutationsUnit && mutationsOwnerships && (
        <UnitMutationsManagementModal
          isOpen={!!mutationsModalUnitId}
          onClose={() => setMutationsModalUnitId(null)}
          unitId={mutationsModalUnitId}
          unitNumber={mutationsUnit.unit_number}
          currentOwners={mutationsOwnerships.map((ownership) => ({
            ownerId: ownership.owner_id,
            ownerName: `${ownership.owners?.first_name || ''} ${ownership.owners?.last_name || ''}`.trim() || 'Sans nom',
            ownershipId: ownership.id,
            shareNumerator: ownership.share_numerator,
            shareDenominator: ownership.share_denominator,
            ownershipType: 'full' as const,
          }))}
        />
      )}
    </Box>
  )
}
